import React from 'react'
import styles from './ActionBar.module.scss'
import clsx from 'clsx'
import { Order } from '../../Types/Order'
import { Actions } from '~shared/lib/ActionsModal/ActionsModal'
import { Button, stylesButton } from '~shared/lib/Button/Button'
import { BellIcon, EditIcon, FilterIcon, ManageMenuIcon } from '../../Assets/svg/Icons'
import { SegmentSwitch, TdsProvider, Button as TdsButton } from '@instacart/tds'
import { IdsProvider } from '@instacart/ids-core'
import { FilterStatus } from '../../Types/Enums'
import { AvailabilityIcon } from './AvailabilityIcon'

type ActionBarProps = {
  setIsDepartmentOrderSettingsVisible: (isVisible: boolean) => void
  setCurrentFilter: (filter: FilterStatus) => void
  currentFilter: FilterStatus
  orders: Order[]
  departmentAvailable: boolean
  canSetDepartmentAvailability: boolean
  itemAvailabilityEnabled: boolean
  setManageMenuOpen: (isVisible: boolean) => void
  setAvailabilityModalOpen: (isVisible: boolean) => void
  setOfflineDevicesModalOpen: (isVisible: boolean) => void
  offlineDeviceCount: number
  deviceOfflineNotificationsEnabled: boolean
  enableKDSOrderEntry: boolean
  canAddKdsOrders: boolean
  onAddOrder: () => void
}

export const ActionBar = ({
  setIsDepartmentOrderSettingsVisible,
  setCurrentFilter,
  currentFilter,
  departmentAvailable,
  canSetDepartmentAvailability,
  itemAvailabilityEnabled,
  setManageMenuOpen,
  setAvailabilityModalOpen,
  setOfflineDevicesModalOpen,
  offlineDeviceCount,
  deviceOfflineNotificationsEnabled,
  enableKDSOrderEntry,
  canAddKdsOrders,
  onAddOrder,
}: ActionBarProps) => {
  return (
    <div className={styles.actionBar}>
      <TdsProvider>
        <IdsProvider>
          <div className={styles.leftButtons}>
            {canSetDepartmentAvailability && (
              <TdsButton
                variant="secondary"
                leftIcon={() => <AvailabilityIcon online={departmentAvailable} />}
                onClick={() => {
                  setAvailabilityModalOpen(true)
                }}
              >
                {departmentAvailable ? 'Accepting' : 'Offline'}
              </TdsButton>
            )}
          </div>
          <div className={styles.filterContainer}>
            <SegmentSwitch
              items={[
                { label: 'Active', value: FilterStatus.Active },
                {
                  label: 'Upcoming',
                  value: FilterStatus.Upcoming,
                },
                {
                  label: 'Complete',
                  value: FilterStatus.Complete,
                },
              ]}
              value={currentFilter}
              onChange={(value: string) => setCurrentFilter(value as unknown as FilterStatus)}
            />
          </div>
          <div className={styles.buttonArea}>
            {deviceOfflineNotificationsEnabled && (
              <Button
                onClick={() => setOfflineDevicesModalOpen(true)}
                className={clsx(
                  styles.bellButton,
                  stylesButton.buttonSecondary,
                  stylesButton.buttonSmall
                )}
                data-testid="offline-devices-button"
                aria-label="Offline devices"
                disabled={false}
              >
                <BellIcon />
                {offlineDeviceCount > 0 && (
                  <span className={styles.bellBadge} data-testid="offline-devices-badge">
                    {offlineDeviceCount}
                  </span>
                )}
              </Button>
            )}
            {enableKDSOrderEntry && canAddKdsOrders && (
              <TdsButton variant="primary" onClick={onAddOrder}>
                Add Order
              </TdsButton>
            )}
            <Actions.Modal
              initialPage="/options"
              actionInProgress={false}
              trigger={({ toggleModal }) => (
                <Button
                  onClick={e => {
                    e.stopPropagation()
                    toggleModal()
                  }}
                  className={clsx(
                    styles.settingsButton,
                    stylesButton.buttonSecondary,
                    stylesButton.buttonSmall
                  )}
                  data-testid="settings-button"
                  disabled={false}
                >
                  <FilterIcon />
                </Button>
              )}
            >
              <Actions.Page header={`Options`} path="/options">
                <Actions.Button
                  action={() => {
                    setIsDepartmentOrderSettingsVisible(true)
                    return true
                  }}
                  icon={<EditIcon />}
                  className={stylesButton.buttonSecondary}
                >
                  Settings
                </Actions.Button>

                {itemAvailabilityEnabled && (
                  <Actions.Button
                    action={() => {
                      setManageMenuOpen(true)
                      return true
                    }}
                    className={stylesButton.buttonSecondary}
                    icon={<ManageMenuIcon />}
                  >
                    Manage Menu
                  </Actions.Button>
                )}
              </Actions.Page>
            </Actions.Modal>
          </div>
        </IdsProvider>
      </TdsProvider>
    </div>
  )
}
